import React, { useState } from 'react';
import styled from 'styled-components';
import { UnmountClosed } from 'react-collapse';
import { formatMoney } from '~/helpers/currency';
import Header from './Header';
import AdditionalServicesLoader from './AdditionalServicesLoader';
import { useTranslation } from '~/Locale';
import { FinanceIcon } from '@kvdbil/components';
import FinanceMonthlyCost from '../../../../../../../shared/components/FinanceCalculator/FinanceMonthlyCost';
import FinanceCalculatorForm from '../../../../../../../shared/components/FinanceCalculator/FinanceCalculatorForm';
import {
  MONTHLY_COST_MONTHS,
  MONTHLY_COST_RESIDUAL_VALUE_MONTHS
} from '~/config/constants';
import { ServiceContainer } from '..';
import { CMSDataObjectPageService } from '~/App/views/ObjectPage/cmsDataHandlers';
import { ServiceStructuredText } from './ServiceStructuredText';

const ServiceInformation = styled.div`
  padding: 1rem;
`;

export interface FinanceServiceProps {
  cmsData?: CMSDataObjectPageService;
  auctionCost: number;
  isBuyNow: boolean;
  initialIsExpanded?: boolean;
  shouldCalculateResidualLoan?: boolean;
}

const FinanceService = ({
  auctionCost,
  cmsData,
  isBuyNow,
  initialIsExpanded = false,
  shouldCalculateResidualLoan = false
}: FinanceServiceProps) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(initialIsExpanded);
  const toggleExpanded = () => setIsExpanded(!isExpanded);

  if (!cmsData) {
    return <AdditionalServicesLoader service="Finance" />;
  }

  return (
    <FinanceMonthlyCost
      price={auctionCost}
      months={
        shouldCalculateResidualLoan
          ? MONTHLY_COST_RESIDUAL_VALUE_MONTHS
          : MONTHLY_COST_MONTHS
      }
      withResidualValue={shouldCalculateResidualLoan}
    >
      {({ value }) => {
        const formattedMonthlyCost = formatMoney({
          value,
          currency: t('SEK')
        });
        const ariaControls = 'additional-service-finance-content';

        return (
          <ServiceContainer>
            <Header
              isExpanded={isExpanded}
              toggleExpanded={toggleExpanded}
              title={cmsData.title ?? ''}
              icon={cmsData?.icon ?? FinanceIcon}
              responsiveImage={cmsData?.image?.responsiveImage}
              description={t(
                'From monthly cost bidding view %1$s',
                formattedMonthlyCost
              )}
              ariaControls={ariaControls}
            />

            <UnmountClosed isOpened={isExpanded}>
              <ServiceInformation id={ariaControls}>
                <ServiceStructuredText data={cmsData?.content} />

                <FinanceCalculatorForm
                  price={auctionCost}
                  buttonUrl={cmsData?.link?.[0]?.url ?? ''}
                  buttonText={cmsData?.link?.[0]?.text ?? ''}
                  costSuffix={t('SEK/month')}
                  footNote={cmsData?.footnote ?? ''}
                  isBuyNow={isBuyNow}
                  withResidualValue={shouldCalculateResidualLoan}
                />
              </ServiceInformation>
            </UnmountClosed>
          </ServiceContainer>
        );
      }}
    </FinanceMonthlyCost>
  );
};

export default FinanceService;
